/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import GraphiQL from 'graphiql';
import fetch from 'isomorphic-fetch';
import { useApi } from '../contexts/ApiHandler';
import TestToolSecurity from './TestToolSecurity';
import EndpointSelector from './EndpointSelector';
const TestToolGraphQL = ({ options, apps }) => {
  const { api, apiHandler } = useApi();
  const { t } = useTranslation();
  const [targetUrlTemplate, setTargetUrlTemplate] = useState(null);
  const [endpoint, setEndpoint] = useState('');
  const [endpoints, setEndpoints] = useState([]);
  const [fetchFail, setFetchFail] = useState(false);
  const [security, setSecurity] = useState({
    userCredentials: {
      username: null,
      password: null
    },
    identification: {
      clientID: null,
      clientSecret: '',
      redirectUri: null,
      externalApiKeys: {}
    },
    flags: {},
    scopes: [],
    authToken: {
      code: '',
      access_token: ''
    }
  });

  useEffect(() => {
    let endpoints = [];

    if (api) {
      endpoints = apiHandler.getEndpoints(api, {});

      if (endpoints && endpoints.length > 0) {
        let basePlusOpPath = '/graphql';
        setEndpoint(endpoints[0]);
        let targetUrlTemplate = apiHandler.buildFullUrl(endpoints[0].url, basePlusOpPath);
        setTargetUrlTemplate(targetUrlTemplate);

        handleCertCheck(targetUrlTemplate);
      }
      setEndpoints(endpoints);
    }
  }, [api, apiHandler]);

  const handleCertCheck = async (targetUrl) => {
    setFetchFail(false);
    try {
      fetch(targetUrl).catch((err) => {
        if (err.message && err.message.includes('Failed to fetch')) {
          setFetchFail(true);
        }
      });
    } catch (err) {
      // no op
    }
  };

  const setHeaders = (security) => {
    let config = security.flags;

    // assume content-type and accept for now
    let accepts = 'application/json';
    let contentTypes = 'application/json';

    let headers = {};

    headers['Content-Type'] = contentTypes;
    headers['Accept'] = accepts;

    if (config.requiresBasicAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresOauth && config.authToken && config.authToken['access_token']) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresHttpBearerToken) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresDigestAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresHobaAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresMutualAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresNegotiateAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresVapidAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresScramShaAuth) {
      headers['Authorization'] = 'not_supported';
    }
    if (config.requiresClientId && config.clientIdLocation === 'header') {
      if (config.clientIdName) {
        headers[config.clientIdName] = security.identification.clientID;
      } else {
        headers['X-IBM-Client-Id'] = security.identification.clientID;
      }
    }
    if (config.requiresClientSecret && config.clientSecretLocation === 'header') {
      if (config.clientSecretName) {
        headers[config.clientSecretName] = security.identification.clientSecret;
      } else {
        headers['X-IBM-Client-Secret'] = security.identification.clientSecret;
      }
    }

    return headers;
  };

  const handleEndpointChange = (event) => {
    let basePlusOpPath = '/graphql';
    let targetUrl = apiHandler.buildFullUrl(event.target.value, basePlusOpPath);
    setEndpoint(event.target.value);
    setTargetUrlTemplate(targetUrl);

    handleCertCheck(targetUrl);
  };

  const handleSecurityChange = (update) => {
    let newSecurity = Object.assign(security, update);
    setSecurity(newSecurity);
  };

  let operation = api.paths['/graphql']['post'];

  let securityConfig = apiHandler.getSecurityConfig(api, operation, null, endpoint);

  let headers = setHeaders(security);

  let mustLogIn = options && options.loginLink && !options.loggedIn;
  let haveApps = apps && apps.length > 0;

  // api not secured or logged in & has apps
  let loadGraphiQL = (!mustLogIn && haveApps) || typeof securityConfig.securityFlows === 'undefined';

  // override logo
  const Logo = () => <span></span>;
  GraphiQL.Logo = Logo;

  return (
    <section className={classNames('tab-content', 'testTool')} aria-label="test-tool">
      <div className="graphql-display__section-tryit-tab-contents">
        <div className="testToolSection testToolEndpoints">
          <div className={classNames('labelText')}>{t('endpoint')}</div>
          <div className="valueText">
            <EndpointSelector id="testtool-endpoint-select" endpoints={endpoints} opPath={'/graphql'} onChange={handleEndpointChange} />
          </div>
        </div>
        {securityConfig && securityConfig.securityFlows && (
          <TestToolSecurity config={securityConfig} options={options} apps={apps} onChange={handleSecurityChange} t={t} />
        )}
        {targetUrlTemplate && loadGraphiQL && (
          <div className={classNames('graphql-api', 'tryit', 'testToolSection')}>
            <GraphiQL
              fetcher={async (graphQLParams) => {
                const data = await fetch('\n' + targetUrlTemplate, {
                  method: 'POST',
                  headers: headers,
                  body: JSON.stringify(graphQLParams)
                });
                let returnData = data.json().catch(() => data.text());
                return returnData;
              }}
            >
              <GraphiQL.Footer>
                {fetchFail && (
                  <div className={'testResponseBody'}>
                    <div className={'body'}>
                      <code className={classNames('boundedSection testResponseContent')}>
                        <div className="contrast responseDetails responseBody">
                          <div className="explorerCorsError">{t('explorer_cors')}</div>
                          <div className="explorerCertificateError">{t('explorer_check_certificate')}</div>
                          <a className="responseUrlLink" target="_blank" rel="noopener noreferrer" href={targetUrlTemplate}>
                            {targetUrlTemplate}
                          </a>
                        </div>
                      </code>
                    </div>
                  </div>
                )}
              </GraphiQL.Footer>
            </GraphiQL>
          </div>
        )}
      </div>
    </section>
  );
};

// needed for finding in tests
TestToolGraphQL.displayName = 'TestGraphQL';
TestToolGraphQL.propTypes = {
  api: PropTypes.object,
  options: PropTypes.object,
  apps: PropTypes.array
};

export default TestToolGraphQL;
